import { useEffect, useState } from 'react';
import { getPortalMessages } from '../../core/api/portal-service';
import { describeApiError } from '../../core/errors/describe-api-error';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { PortalMessage } from '../../core/models/portal-message';
import { Alert } from '../../ui/alert';
import { Card } from '../../ui/card';
import { Skeleton } from '../../ui/skeleton';
import { MessageThread } from './message-thread';

/**
 * The questions channel.
 *
 * **It loads its own thread, and only when opened.** Reading the thread marks the staff replies read
 * on the server, so fetching it with the rest of the portal would clear a reply the customer never
 * looked at — harmless while everything was one page, and wrong once it is a tab they may not open.
 *
 * It opens on the newest messages; older ones are fetched a slice at a time, by cursor, on request.
 */
export function MessagesTab() {
    const { t } = useTranslation();

    const [messages, setMessages] = useState<PortalMessage[] | null>(null);
    const [hasEarlier, setHasEarlier] = useState(false);
    const [loadingEarlier, setLoadingEarlier] = useState(false);
    const [error, setError] = useState<TranslationKey | null>(null);

    useEffect(() => {
        const controller = new AbortController();

        getPortalMessages(null, controller.signal)
            .then((thread) => {
                setMessages(thread.messages);
                setHasEarlier(thread.hasEarlier);
            })
            .catch((cause: unknown) => {
                if (!controller.signal.aborted) {
                    setError(describeApiError(cause));
                    setMessages([]);
                }
            });

        return () => controller.abort();
    }, []);

    async function loadEarlier() {
        const oldest = messages?.[0];

        if (loadingEarlier || oldest === undefined) {
            return;
        }

        setLoadingEarlier(true);
        setError(null);

        try {
            const earlier = await getPortalMessages(oldest.id);

            setMessages((current) => [...earlier.messages, ...(current ?? [])]);
            setHasEarlier(earlier.hasEarlier);
        } catch (cause: unknown) {
            setError(describeApiError(cause));
        } finally {
            setLoadingEarlier(false);
        }
    }

    if (messages === null) {
        return (
            <Card>
                <div role="status" aria-live="polite" className="space-y-4 p-5">
                    <span className="sr-only">{t('portal.loading')}</span>
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-16" />
                    <Skeleton className="h-24" />
                </div>
            </Card>
        );
    }

    return (
        <div className="space-y-4">
            {error !== null && <Alert>{t(error)}</Alert>}

            <MessageThread
                messages={messages}
                clientCaseId={null}
                onPosted={(message) => setMessages((current) => [...(current ?? []), message])}
                hasEarlier={hasEarlier}
                loadingEarlier={loadingEarlier}
                onLoadEarlier={() => void loadEarlier()}
            />
        </div>
    );
}
